import React, { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { EntityType, PaymentService } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import PayableCard from "../../../newComponents/listings/listing-cards/payables";
import SingleDropdown from "../../../newComponents/singleDropdown/SingleDropdown";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import EmptyState from "../../../newComponents/emptystate";
import { GetImages } from "../../../utils/GetImages";
import ManageAutoPayForm from "./ManageAutoPayForm";

const ChevronIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginLeft: "6px" }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#64748B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface TenantPaymentsTabContentProps {
  filteredRows: any[];
  loading: boolean;
  fetchData: (page?: number) => void;
  sentryRef: (node?: Element | null) => void;
  selectedFilter: string;
  menuItems: any[];
  tenantId?: number;
}

const TenantPaymentsTabContent: React.FC<TenantPaymentsTabContentProps> = ({
  filteredRows,
  loading,
  fetchData,
  sentryRef,
  selectedFilter,
  menuItems,
  tenantId,
}) => {
  const [isAutoPayOpen, setIsAutoPayOpen] = useState(false);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<
    { Id: number; Name: string }[]
  >([]);

  const autoPayFormId = "tenant-manage-auto-pay-form";
  const autoPayFormMethods = useForm({
    defaultValues: {
      isEnabled: "true",
      accountId: "",
      billingPeriodId: "",
      dayOfMonth: "",
    },
  });

  useEffect(() => {
    if (!tenantId) return;
    apiHelper(
      PaymentService.getApiPaymentMethodTenantlist,
      { showNotification: false },
      tenantId
    )
      .then((res) => {
        const raw = res?.data?.success || [];
        setPaymentMethodOptions(
          raw.map((item: any) => ({
            Id: item.id,
            Name: item.accountName,
          }))
        );
      })
      .catch(() => setPaymentMethodOptions([]));
  }, [tenantId]);

  const handleSaveAutoPay = async (values: any) => {
    try {
      const payload = {
        entityId: tenantId,
        entityTypeId: EntityType.Tenant,
        isEnabled: values.isEnabled === "true",
        accountId: values.accountId ? Number(values.accountId) : undefined,
        billingPeriodId: values.billingPeriodId
          ? Number(values.billingPeriodId)
          : undefined,
        dayOfMonth: values.dayOfMonth ? Number(values.dayOfMonth) : undefined,
      };
      // TODO: Replace with actual API when available
      setIsAutoPayOpen(false);
    } catch (err) {
      console.error("Manage Auto Pay error", err);
    }
  };

  return (
    <div className="tenant-list-wrapper">
      <div className="payment-tab-heading">
        <h2>Payment</h2>
        <div className="payment-tab-heading__actions">
          <SingleDropdown
            triggerText={selectedFilter}
            menuItems={menuItems}
            triggerIconPosition="right"
            triggerIcon={<ChevronIcon />}
          />
          <SimpleButton
            label="Manage Auto Pay"
            onClick={() => setIsAutoPayOpen(true)}
          />
        </div>
      </div>

      <div className="tenant-payment-list">
        {filteredRows.length === 0 && !loading ? (
          <div className="tenant-empty-filter-state">
            {selectedFilter !== "All Payments" ? (
              <EmptyState
                icon={GetImages.emptyStatePayment}
                heading="No Payment Found with Applied Filters"
                isButton={false}
                className="empty-state-wrapper"
              />
            ) : (
              <EmptyState
                icon={GetImages.emptyStatePayment}
                className="bg-transparent"
                isBorder={false}
                heading="No Payments Available"
                isButton={false}
              />
            )}
          </div>
        ) : (
          <div className="tanant-payment-card-list">
            <PayableCard
              id={tenantId || 0}
              type="tenant"
              className="tenant-dashboard-payable-card"
              isFooter={false}
              dataHookState={{ rows: filteredRows, loading, fetchData }}
            />
            <div ref={sentryRef} />
          </div>
        )}
      </div>

      <RightSideModal
        isOpen={isAutoPayOpen}
        onClose={() => setIsAutoPayOpen(false)}
        title="Manage Auto Pay"
        buttonlabel="Save"
        formId={autoPayFormId}
        maxWidth={480}
        isDirty={autoPayFormMethods.formState.isDirty}
      >
        <FormProvider {...autoPayFormMethods}>
          <ManageAutoPayForm
            formId={autoPayFormId}
            onSubmit={handleSaveAutoPay}
            accountOptions={paymentMethodOptions}
          />
        </FormProvider>
      </RightSideModal>
    </div>
  );
};

export default TenantPaymentsTabContent;
